import { createContext, useContext, useState } from 'react'
import { Dribbble } from 'lucide-react'

// Sport icon components
export function NFLIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className}>
      <ellipse cx="12" cy="12" rx="9" ry="5.5" stroke="currentColor" strokeWidth="2" />
      {/* Vertical seam */}
      <path d="M12 6.5v11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      {/* 3 horizontal laces */}
      <path d="M9.5 9.5h5M9.5 12h5M9.5 14.5h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

export function NBAIcon({ className }) {
  return <Dribbble className={className} />
}

export function GolfIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className}>
      {/* Flag pole */}
      <path d="M12 4v14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      {/* Flag */}
      <path d="M12 4l6 3-6 3V4z" fill="currentColor" opacity="0.3" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      {/* Hole/ground */}
      <ellipse cx="12" cy="19" rx="4" ry="1.5" stroke="currentColor" strokeWidth="1.5" />
      {/* Golf ball */}
      <circle cx="7" cy="17" r="2" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  )
}

export function SportIcon({ sport, className }) {
  if (sport === 'nfl') return <NFLIcon className={className} />
  if (sport === 'golf') return <GolfIcon className={className} />
  return <NBAIcon className={className} />
}

// Sport context for app-wide sport selection
const SportContext = createContext({
  sport: 'nfl',
  setSport: () => {},
})

export const useSport = () => useContext(SportContext)

// Sport configurations
export const SPORTS = {
  nfl: {
    id: 'nfl',
    name: 'NFL',
    fullName: 'National Football League',
    primaryColor: 'accent-cyan',
    season: '2025-26 Playoffs',
    description: 'Pro Football',
  },
  nba: {
    id: 'nba',
    name: 'NBA',
    fullName: 'National Basketball Association',
    primaryColor: 'accent-purple',
    season: '2025-26 Season',
    description: 'Pro Basketball',
  },
  golf: {
    id: 'golf',
    name: 'Golf',
    fullName: 'PGA Tour',
    primaryColor: 'accent-green',
    season: '2026 Season',
    description: 'Head-to-Head Matchups',
  },
}

export function SportProvider({ children }) {
  // Default to NFL, persist choice
  const [sport, setSportState] = useState(() => {
    const saved = localStorage.getItem('selectedSport')
    return saved && SPORTS[saved] ? saved : 'nfl'
  })

  const setSport = (newSport) => {
    if (SPORTS[newSport]) {
      setSportState(newSport)
      localStorage.setItem('selectedSport', newSport)
    }
  }

  const sportConfig = SPORTS[sport]

  return (
    <SportContext.Provider value={{ sport, setSport, sportConfig, SPORTS }}>
      {children}
    </SportContext.Provider>
  )
}

export default SportContext
